import { useState, useCallback } from 'react';
import { Box, Flex, Grid, Heading, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { MdSearchOff } from 'react-icons/md';
import { useLocation } from 'wouter';
import { useHomeSearchParams } from '@/hooks/useHomeSearchParams';
import { useScrollRestore } from '@/hooks/useScrollRestore';
import { homeSearchKey } from '@/utils/homeSearchUrl';
import { saveHomeScroll } from '@/utils/homeScroll';
import { useRecipes } from '@/lib/recipeService';
import { useAllCollections } from '@/lib/collectionService';
import { ROUTES } from '@/types';
import BrowseHeader from '@/components/BrowseHeader';
import BottomNavBar from '@/components/BottomNavBar';
import FloatingAIChefButton from '@/components/FloatingAIChefButton';
import ProfileSheet from '@/components/ProfileSheet';
import RecipeCard from '@/components/RecipeCard';
import HomeCollectionsGrid from '@/components/HomeCollectionsGrid';
import HomeImportRow from '@/components/HomeImportRow';
import HomeRecentRail from '@/components/HomeRecentRail';
import { CollectionTile } from '@/components/CollectionTile';

export default function Home() {
  const [, navigate] = useLocation();
  // Search/filter state lives in the URL so Back restores it (search → recipe →
  // back keeps the results) and searches are shareable.
  const {
    searchTerm,
    debouncedSearch,
    selectedTagsByGroup,
    selectedRatings,
    setSearchTerm,
    toggleTag,
    toggleRating,
    clearFilters,
  } = useHomeSearchParams();
  const [filterTrigger, setFilterTrigger] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const openFilters = useCallback(() => setFilterTrigger(n => n + 1), []);

  const selectedTagsFlat = Object.values(selectedTagsByGroup).flat();
  const totalActiveFilters = selectedTagsFlat.length + (selectedRatings.length > 0 ? 1 : 0);
  const effectiveMinRating = selectedRatings.length > 0 ? Math.min(...selectedRatings) : undefined;

  const isSearching =
    debouncedSearch.trim().length > 0 || selectedTagsFlat.length > 0 || selectedRatings.length > 0;

  const { collections: matchingCollections } = useAllCollections({
    search: isSearching && debouncedSearch.trim().length > 0 ? debouncedSearch : undefined,
  });

  const {
    recipes: searchResults,
    isLoading: isSearchLoading,
    isError: isSearchError,
  } = useRecipes({
    search: debouncedSearch,
    tagsByGroup: selectedTagsByGroup,
    minRating: effectiveMinRating,
  });

  const scrollKey = homeSearchKey({ searchTerm: debouncedSearch, selectedTagsByGroup, selectedRatings });
  const hasResults = searchResults.length > 0 || matchingCollections.length > 0;
  useScrollRestore(scrollKey, isSearching && !isSearchLoading && hasResults);

  const handleRecipeClick = (recipeId: string) => {
    saveHomeScroll(scrollKey);
    navigate(ROUTES.RECIPE(recipeId));
  };

  const handleCollectionClick = (collectionId: string) => {
    saveHomeScroll(scrollKey);
    navigate(ROUTES.RECIPE_GROUP(collectionId));
  };

  return (
    <Box minH="100vh" bg="bg.canvas" pb={{ base: '80px', lg: 0 }}>
      <BrowseHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        placeholder="Search recipes, collections…"
        showFilter
        selectedTags={selectedTagsFlat}
        onToggleTag={toggleTag}
        selectedRatings={selectedRatings}
        onToggleRating={toggleRating}
        onClearFilters={clearFilters}
        totalActiveFilters={totalActiveFilters}
        filterTrigger={filterTrigger}
        onFilterClick={openFilters}
        onProfileClick={() => setProfileOpen(true)}
      />

      <Box px={{ base: 0, lg: 8 }} pt={3}>
        {isSearching ? (
          <Box px={{ base: 4, lg: 0 }} pt={2}>
            {isSearchLoading ? (
              <Flex justify="center" py={10}>
                <Spinner size="lg" color="accent.secondary" />
              </Flex>
            ) : isSearchError ? (
              <Text color="text.muted" textAlign="center" py={10}>
                Something went wrong. Try again.
              </Text>
            ) : searchResults.length > 0 || matchingCollections.length > 0 ? (
              <VStack gap={4} align="stretch">
                {matchingCollections.length > 0 && (
                  <Box>
                    <Text
                      fontFamily="body"
                      fontWeight="600"
                      fontSize="13px"
                      color="text.muted"
                      mb={2}
                    >
                      Collections
                    </Text>
                    <Grid
                      templateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
                      gap={3}
                    >
                      {matchingCollections.map((collection) => (
                        <CollectionTile
                          key={collection.id}
                          collection={collection}
                          onClick={() => handleCollectionClick(collection.id)}
                          height="90px"
                        />
                      ))}
                    </Grid>
                  </Box>
                )}
                {searchResults.length > 0 && (
                  <>
                    {matchingCollections.length > 0 && (
                      <Text fontFamily="body" fontWeight="600" fontSize="13px" color="text.muted">
                        Recipes
                      </Text>
                    )}
                    <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={2}>
                      {searchResults.map(recipe => (
                        <RecipeCard
                          key={recipe.id}
                          recipe={recipe}
                          onRecipeClick={handleRecipeClick}
                        />
                      ))}
                    </Grid>
                  </>
                )}
              </VStack>
            ) : (
              <VStack py={16} gap={3} textAlign="center">
                <Box
                  w="52px"
                  h="52px"
                  borderRadius="14px"
                  bg="bg.muted"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mx="auto"
                >
                  <Icon as={MdSearchOff} boxSize={6} color="text.muted" />
                </Box>
                <Heading fontFamily="heading" fontWeight="800" fontSize="16px" color="text.primary">
                  No results
                </Heading>
                <Text
                  fontFamily="body"
                  fontWeight="300"
                  fontSize="13px"
                  color="text.muted"
                  lineHeight="1.6"
                >
                  {debouncedSearch
                    ? `Nothing matched "${debouncedSearch}". Try a different word.`
                    : 'No recipes match the current filters.'}
                </Text>
              </VStack>
            )}
          </Box>
        ) : (
          <>
            <HomeCollectionsGrid />
            <HomeImportRow />
            <HomeRecentRail />
          </>
        )}
      </Box>

      <ProfileSheet isOpen={profileOpen} onClose={() => setProfileOpen(false)} />
      <BottomNavBar />
      <FloatingAIChefButton />
    </Box>
  );
}
